import { useEffect, useRef, useState, useCallback } from 'react';
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';

export const usePoseDetection = (options = {}) => {
  const {
    modelComplexity = 0,
    minDetectionConfidence = 0.5,
    minTrackingConfidence = 0.5,
    onPoseDetected = null
  } = options;

  const poseLandmarkerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  const getModelPath = (complexity) => {
    const models = {
      0: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
      1: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task',
      2: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task'
    };
    return models[complexity] || models[0];
  };

  useEffect(() => {
    const initializePoseLandmarker = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        );

        const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: getModelPath(modelComplexity),
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: minDetectionConfidence,
          minPosePresenceConfidence: minDetectionConfidence,
          minTrackingConfidence: minTrackingConfidence,
          outputSegmentationMasks: false
        });

        poseLandmarkerRef.current = poseLandmarker;
        setIsInitialized(true);
      } catch (err) {
        setError(err.message);
        console.error('Failed to initialize PoseLandmarker:', err);
      }
    };

    initializePoseLandmarker();
  }, [modelComplexity, minDetectionConfidence, minTrackingConfidence]);

  const detectPose = useCallback(async (videoElement) => {
    if (!poseLandmarkerRef.current || !videoElement) return null;

    try {
      const results = poseLandmarkerRef.current.detectForVideo(
        videoElement,
        performance.now()
      );

      if (onPoseDetected && results) {
        onPoseDetected(results);
      }

      return results;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [onPoseDetected]);

  return {
    poseLandmarker: poseLandmarkerRef.current,
    isInitialized,
    error,
    detectPose
  };
};
